import * as React from 'react';
import { format } from 'date-fns';
import { Calendar } from '../../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Button } from '../../../components/ui/button';
import { DateRange, CaptionProps } from 'react-day-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

interface CustomCaptionProps extends CaptionProps {
  displayedMonth: Date;
  setDisplayedMonth: (date: Date) => void;
}

const CustomCaption: React.FC<CustomCaptionProps> = ({
  displayedMonth,
  setDisplayedMonth,
}) => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 40;
  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i
  );

  return (
    <div className="flex justify-center gap-2 px-2 py-1 ">
      <Select
        value={displayedMonth.getFullYear().toString()}
        onValueChange={(year) => {
          const newDate = new Date(displayedMonth);
          newDate.setFullYear(parseInt(year));
          setDisplayedMonth(newDate);
        }}
      >
        <SelectTrigger className="w-[80px] h-8 text-sm">
          <SelectValue placeholder={displayedMonth.getFullYear().toString()} />
        </SelectTrigger>
        <SelectContent className="max-h-48 overflow-auto">
          {years.map((year) => (
            <SelectItem key={year} value={year.toString()}>
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={displayedMonth.getMonth().toString()}
        onValueChange={(month) => {
          const newDate = new Date(displayedMonth);
          newDate.setMonth(parseInt(month));
          setDisplayedMonth(newDate);
        }}
      >
        <SelectTrigger className="w-[110px] h-8 text-sm">
          <SelectValue placeholder={format(displayedMonth, 'MMMM')} />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 12 }, (_, i) => (
            <SelectItem key={i} value={i.toString()}>
              {format(new Date(displayedMonth.getFullYear(), i, 1), 'MMMM')}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export function StudyDateRangeFilter({
  value,
  onChange,
}: {
  value: DateRange | undefined;
  onChange: (value: DateRange | undefined) => void;
}) {
  const [range, setRange] = React.useState<DateRange | undefined>(value);
  const [editingBoundary, setEditingBoundary] = React.useState<'from' | 'to'>(
    'from'
  );
  const [displayedMonth, setDisplayedMonth] = React.useState<Date>(
    value?.from ?? new Date()
  );
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setRange(value);
    if (value?.from) setDisplayedMonth(value.from);
  }, [value]);

  const handleSelect = (selected: Date | undefined) => {
    if (!selected) return;

    if (!range) {
      const newRange = { from: selected, to: undefined };
      setRange(newRange);
      onChange(newRange);
      setEditingBoundary('to');
      setDisplayedMonth(selected);
      return;
    }

    if (editingBoundary === 'from') {
      const newRange = { from: selected, to: range.to };
      setRange(newRange);
      onChange(newRange);
      setEditingBoundary('to');
      setDisplayedMonth(selected);
    } else {
      const newRange = { from: range.from, to: selected };
      setRange(newRange);
      onChange(newRange);
      setEditingBoundary('from');
      setDisplayedMonth(selected);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="flex h-8 w-full justify-start rounded border border-input bg-background px-2 py-1 font-normal text-foreground"
          onClick={() => setOpen(true)}
        >
          {range?.from && range?.to
            ? `${format(range.from, 'yyyy-MM-dd')} - ${format(
                range.to,
                'yyyy-MM-dd'
              )}`
            : 'Pick a date range'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-4">
        <div className="mb-4 flex gap-2">
          <Button
            type="button"
            variant={editingBoundary === 'from' ? 'secondary' : 'outline'}
            onClick={() => setEditingBoundary('from')}
          >
            Start Date
          </Button>
          <Button
            type="button"
            variant={editingBoundary === 'to' ? 'secondary' : 'outline'}
            onClick={() => setEditingBoundary('to')}
          >
            End Date
          </Button>
        </div>
        <Calendar
          mode="single"
          selected={editingBoundary === 'from' ? range?.from : range?.to}
          onSelect={handleSelect}
          month={displayedMonth}
          onMonthChange={setDisplayedMonth}
          components={{
            Caption: (props) => (
              <CustomCaption
                {...props}
                displayedMonth={displayedMonth}
                setDisplayedMonth={setDisplayedMonth}
              />
            ),
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export function SingleDateFilter({
  value,
  onChange,
  placeholder = 'Pick a date',
}: {
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
  placeholder?: string;
}) {
  const [selected, setSelected] = React.useState<Date | undefined>(value);
  const [displayedMonth, setDisplayedMonth] = React.useState<Date>(
    value ?? new Date()
  );
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setSelected(value);
    if (value) setDisplayedMonth(value);
  }, [value]);

  const handleSelect = (date: Date | undefined) => {
    setSelected(date);
    onChange(date);
    if (date) setDisplayedMonth(date);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="flex h-8 w-full justify-start rounded border border-input bg-background px-2 py-1 font-normal text-foreground"
          onClick={() => setOpen(true)}
        >
          {selected ? format(selected, 'yyyy-MM-dd') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-4">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          month={displayedMonth}
          onMonthChange={setDisplayedMonth}
          components={{
            Caption: (props) => (
              <CustomCaption
                {...props}
                displayedMonth={displayedMonth}
                setDisplayedMonth={setDisplayedMonth}
              />
            ),
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
